import { useEffect, useRef, useState } from 'react';
import type { PointerEvent, ReactNode } from 'react';
import { useDeviceScale } from './deviceScaleContext';

interface SwipeCarouselProps {
  /** One slide each; the track owns no slide content. */
  slides: ReactNode[];
  /** Slide width as a flex basis. Below 100% the neighbours peek. */
  slideWidth?: string;
  /** Space between slides, any CSS length. */
  gap?: string;
  /** Left/right room around the slides, so an inset page still peeks. */
  inset?: string;
  /** 0-based page to show. Omit to let the carousel own its index. */
  value?: number;
  /** Fires once per settled page turn, including a snap back to the same
   * page — never mid-drag. */
  onChange?: (index: number) => void;
  /** Off when the host paints its own pager. */
  dots?: boolean;
  /** Page-dot distance from the track's bottom edge. */
  dotsBottom?: number;
  /** Dots follow the host's text colour unless told otherwise. */
  dotColor?: string;
}

/** Pointer travel that commits the gesture to an axis. */
const AXIS_THRESHOLD = 4;
/** Travel that turns a release into a page turn rather than a snap back. */
const COMMIT_THRESHOLD = 20;
/** Idle time before a wheel/trackpad scroll snaps to the nearest slide. */
const SETTLE_DELAY = 140;

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

/**
 * Horizontal pager with synced page dots. A drag moves at most one page and
 * commits on release, so a flick can never land between slides or skip past
 * several. Knows nothing about what it pages through.
 *
 * The one swipe implementation: a consumer needing different behaviour
 * (controlled index, peeking neighbours, an external pager) gets a parameter
 * here — never a second carousel.
 */
export const SwipeCarousel = ({
  slides,
  slideWidth = '100%',
  gap,
  inset,
  value,
  onChange,
  dots = true,
  dotsBottom = 12,
  dotColor = 'currentColor',
}: SwipeCarouselProps) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const scale = useDeviceScale();
  const [index, setIndex] = useState(0);
  const lastIndex = slides.length - 1;
  // Read by the controlled-value effect, which must not re-run on the index.
  // Synced first, so that effect sees this render's index.
  const indexRef = useRef(0);
  useEffect(() => {
    indexRef.current = index;
  }, [index]);

  // Stride is read from an observer, never during a gesture: reading layout
  // right after writing scrollLeft forces a reflow per event. Measured slide
  // to slide, so gaps need no parsing.
  const stride = useRef(0);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const measure = () => {
      const [first, second] = track.children;
      if (!(first instanceof HTMLElement)) return;
      stride.current =
        second instanceof HTMLElement
          ? second.offsetLeft - first.offsetLeft
          : first.offsetWidth;
    };
    const observer = new ResizeObserver(measure);
    observer.observe(track);
    measure();
    return () => observer.disconnect();
  }, [slides.length]);

  const drag = useRef({
    down: false,
    /** Which axis the gesture committed to, decided on the first move. */
    axis: null as null | 'x' | 'y',
    pointerId: -1,
    startX: 0,
    startY: 0,
    /** Page the gesture started on; the drag stays within its neighbours. */
    fromIndex: 0,
    offset: 0,
  });

  /** Scroll offset an animation is heading for, so idle-settling stays
   * out of its way until it arrives. */
  const commitTarget = useRef<number | null>(null);
  const settleTimer = useRef<number>();

  const scrollTo = (target: number) => {
    const track = trackRef.current;
    if (!track) return;
    const left = target * stride.current;
    // Already there: no scroll event would follow, and a target left
    // standing would swallow the next settle.
    commitTarget.current = Math.abs(track.scrollLeft - left) < 1 ? null : left;
    track.scrollTo({ left, behavior: 'smooth' });
  };

  /**
   * A settled page choice — the only thing the host hears about. An
   * unchanged page stays quiet, or a controlled host would hear its own
   * scroll back as a fresh choice; `always` overrides that for a drag,
   * where a spring-back is still deliberate input.
   */
  const commit = (target: number, always = false) => {
    scrollTo(target);
    setIndex(target);
    if (always || target !== indexRef.current) onChange?.(target);
  };

  // A controlled host (autoplay, an external pager) moves the track; its own
  // commits arrive back here as an unchanged value and scroll nowhere.
  useEffect(() => {
    if (value != null && value !== indexRef.current) {
      setIndex(value);
      scrollTo(value);
    }
  }, [value]);

  const onScroll = () => {
    const track = trackRef.current;
    const width = stride.current;
    if (!track || !width) return;
    const at = track.scrollLeft / width;

    if (commitTarget.current !== null) {
      // Mid-flight positions say nothing: rounding them would drag the index
      // back to the page being left, and a controlled host would then find
      // its next value already "current" and never move.
      if (Math.abs(track.scrollLeft - commitTarget.current) >= 1) return;
      commitTarget.current = null;
    }
    // Dots track the scroll; the host only hears settled pages.
    setIndex(Math.round(at));
    // Wheel and trackpad scroll the track freely; only they need settling.
    if (drag.current.down) return;
    window.clearTimeout(settleTimer.current);
    settleTimer.current = window.setTimeout(
      () => commit(Math.round(at)),
      SETTLE_DELAY
    );
  };

  const endDrag = () => {
    const state = drag.current;
    if (!state.down) return;
    const track = trackRef.current;
    // Capture outlives a release the page never saw; left held, the next
    // gesture starts retargeted to a track that thinks it is idle.
    if (track?.hasPointerCapture(state.pointerId)) {
      track.releasePointerCapture(state.pointerId);
    }
    state.down = false;

    if (state.axis !== 'x') return;
    state.axis = null;
    const turned =
      Math.abs(state.offset) > COMMIT_THRESHOLD ? Math.sign(state.offset) : 0;
    commit(clamp(state.fromIndex + turned, 0, lastIndex), true);
  };

  // The window listeners bind once, so they read the current `endDrag`
  // through a ref rather than the closure captured at mount.
  const endDragRef = useRef(endDrag);
  useEffect(() => {
    endDragRef.current = endDrag;
  });

  // A release outside the window, or in another window entirely, never
  // reaches the element — without these the drag stays armed and the next
  // pointer entry drags a page that was never grabbed.
  useEffect(() => {
    const release = () => endDragRef.current();
    const onWindowMove = (e: globalThis.PointerEvent) => {
      if (e.buttons === 0) release();
    };
    window.addEventListener('pointerup', release, true);
    window.addEventListener('pointercancel', release, true);
    window.addEventListener('pointermove', onWindowMove, true);
    window.addEventListener('blur', release);
    return () => {
      window.removeEventListener('pointerup', release, true);
      window.removeEventListener('pointercancel', release, true);
      window.removeEventListener('pointermove', onWindowMove, true);
      window.removeEventListener('blur', release);
      window.clearTimeout(settleTimer.current);
    };
  }, []);

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== 'mouse' || e.button !== 0) return;
    const track = trackRef.current;
    const width = stride.current;
    // Grabbing mid-animation: stop it where it stands, or it keeps pulling
    // against the new drag.
    window.clearTimeout(settleTimer.current);
    commitTarget.current = null;
    if (track) track.scrollTo({ left: track.scrollLeft, behavior: 'auto' });
    drag.current = {
      down: true,
      axis: null,
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      fromIndex: width ? Math.round((track?.scrollLeft ?? 0) / width) : 0,
      offset: 0,
    };
  };

  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const state = drag.current;
    const track = trackRef.current;
    const width = stride.current;
    if (!state.down || !track || !width) return;
    // Track geometry is content pixels, pointer travel is screen pixels;
    // a scaled host would otherwise page faster than the cursor moves.
    const dx = (e.clientX - state.startX) / scale;
    const dy = (e.clientY - state.startY) / scale;

    if (!state.axis) {
      if (Math.max(Math.abs(dx), Math.abs(dy)) < AXIS_THRESHOLD) return;
      state.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      // Claim the pointer, or the host frame's vertical drag captures it
      // mid-swipe and the track stops tracking the cursor until release.
      if (state.axis === 'x') track.setPointerCapture(e.pointerId);
    }
    if (state.axis !== 'x') return;

    // Capture retargets moves here but they still bubble to the frame.
    e.stopPropagation();
    e.preventDefault();
    // A gesture reaches its neighbours and no further, so travelling far
    // out of bounds changes nothing and reversing responds immediately.
    state.offset = clamp(-dx, -width, width);
    track.scrollLeft = clamp(
      (state.fromIndex + state.offset / width) * width,
      0,
      lastIndex * width
    );
  };

  return (
    <div style={{ position: 'relative' }}>
      <div
        ref={trackRef}
        className="phone-frame-scroll"
        // Without user-select a drag starts a text selection instead, and the
        // selection's own autoscroll throws the track to one end.
        style={{
          display: 'flex',
          overflowX: 'auto',
          userSelect: 'none',
          gap,
        }}
        onScroll={onScroll}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onDragStart={(e) => e.preventDefault()}
      >
        {slides.map((slide, i) => (
          <div
            key={i}
            style={{
              flex: `0 0 ${slideWidth}`,
              minWidth: 0,
              // Room for the inset rides on the outer slides, not on the
              // track: track padding is outside the scrollable range, so the
              // last slide could never reach the position the first sits in.
              marginInlineStart: i === 0 ? inset : undefined,
              marginInlineEnd: i === lastIndex ? inset : undefined,
            }}
          >
            {slide}
          </div>
        ))}
      </div>

      {dots && slides.length > 1 && (
        <div
          style={{
            position: 'absolute',
            bottom: dotsBottom,
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',
            gap: 6,
          }}
        >
          {slides.map((_, i) => (
            <div
              key={i}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => commit(i)}
              style={{
                width: 6,
                height: 6,
                borderRadius: 3,
                background: dotColor,
                opacity: i === index ? 1 : 0.4,
                cursor: 'pointer',
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};
